import React, { Fragment, useCallback, useEffect, useMemo } from 'react';
import classes from './SocialProfileSelectorListWithGroups.module.scss';
import {
  SelectedSocialProfileValue,
  SocialProfileSelectorListViewProps,
  TikTokComponentPropValues
} from '../SocialProfileSelectorList/SocialProfileSelectorList.view';
import { MappedProfileType } from '../SocialProfileListItem/SocialProfileListItem.view';
import { CheckboxList } from '../../forms/CheckboxList';
import { SocialProfile } from '../SocialProfile';
import { profilesHelpers } from '../../../modules/Settings/Profiles/Profiles.helpers';
import { SocialProfileFieldsFragment } from '../../../models/graphql';
import { useWatch } from 'react-hook-form';
import classNames from 'classnames';
import { Button } from '../../actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { Link } from 'react-router-dom';
import { idGenerator } from '../../../helpers/idGenerator';
import { SelectAllCheckboxes } from '../../actions/SelectAllCheckboxes';
import { isArray } from 'lodash';
import { SocialProfileSelectorListItem } from '../SocialProfileSelectorListItem';
import { MakeRequired, SortFunction } from '../../../models/basic-objects';
import { sortedSocialProfilesTypes } from '../../../apollo/socialProfilesPolicy';
import { SocialNetworksEnum } from '../../../constants/social-networks';

export type ProfileGroupsObject = {
  [P in MakeRequired<MappedProfileType, '__typename'>['__typename']]?: MappedProfileType[];
};

type ProfileGroupsObjectMap = Map<keyof ProfileGroupsObject, MappedProfileType[]>;

const sortKeyFunction: SortFunction<keyof ProfileGroupsObject> = (a, b) => {
  const indexA = [...sortedSocialProfilesTypes, 'Bitly'].indexOf(a);
  const indexB = [...sortedSocialProfilesTypes, 'Bitly'].indexOf(b);
  if (indexA < indexB) {
    return -1;
  } else if (indexA > indexB) {
    return 1;
  }
  return 0;
};

export const SocialProfileSelectorListWithGroupsView = (props: SocialProfileSelectorListViewProps): JSX.Element => {
  const { profiles, selectedProfiles, selectAllValidation, useFormInstance, formFieldName } = props;

  const { setValue: setFormValue, getValues: getFormValues } = useFormInstance;

  // we map profiles by groups, in order to create arrays of profiles grouped by type
  const profilesGroups: ProfileGroupsObjectMap = useMemo(() => {
    const groupObject = profiles.reduce((prev, curr) => {
      if (!curr.__typename) return prev;
      if (!prev.hasOwnProperty(curr.__typename)) {
        prev[curr.__typename] = [curr];
      } else {
        prev[curr.__typename]?.push(curr);
      }
      return prev;
    }, {} as ProfileGroupsObject);
    const keys = Object.keys(groupObject) as Array<keyof ProfileGroupsObject>;
    keys.sort(sortKeyFunction);
    // we need to use Map, becaus emap will make sure we persist key order
    const sorted: Map<keyof ProfileGroupsObject, MappedProfileType[]> = new Map();
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const value = groupObject[key];
      value && sorted.set(key, value);
    }
    // we'll return Iterable map with key & value props
    return sorted;
  }, [profiles]);

  const isSelectedProfile = (profile: MappedProfileType): boolean =>
    !!selectedProfiles?.map((elem: SelectedSocialProfileValue) => elem.profileId).includes(profile.id);

  const pnSelectedBoard = (profile: MappedProfileType): string | null => {
    const index = selectedProfiles?.findIndex(elem => elem.profileId === profile.id && elem.pnBoardId) || -1;
    if (index > -1) {
      return selectedProfiles?.[index].pnBoardId || null;
    }
    return null;
  };

  const tiktokValues = (profile: MappedProfileType): TikTokComponentPropValues | null => {
    const index = selectedProfiles?.findIndex(elem => elem.profileId === profile.id && elem.tikTok);
    if (index !== undefined && index > -1) {
      const { tikTok } = selectedProfiles?.[index] || { tikTok: undefined };
      if (tikTok) {
        const {
          allowedActions: tikTokActionValue,
          promotion: tikTokPromotionValue,
          privacyTarget: tikTokTargetValue
        } = tikTok;
        return {
          tikTokTargetValue,
          tikTokActionValue,
          tikTokPromotionValue
        };
      } else {
        return {
          tikTokTargetValue: null,
          tikTokActionValue: [],
          tikTokPromotionValue: []
        };
      }
    }
    return null;
  };

  const profileGroupsKeys = useMemo(
    () => Array.from(profilesGroups.keys()).map(key => `${formFieldName}_${key}`),
    [formFieldName, profilesGroups]
  );

  const allProfilesValues: Array<string | boolean>[] | undefined = useWatch({
    control: useFormInstance.control,
    name: profileGroupsKeys
  });

  useEffect(() => {
    // if we have set the allProfilesValue by select all checkbox, it might lead us to array or array containing
    // undefined value. if that is the case, we don't want to run this use effect, and we should wait nex iteration
    // where those values are set.
    if (allProfilesValues.reduce((prev, curr) => !(!prev || !curr), true) === false) return;
    // next line will map allProfilesValues aray of arrays to plain array, that our main formName contains
    const profilesValues = allProfilesValues.flatMap(item => item.flatMap(subitem => (subitem ? subitem : false)));
    const formValues = getFormValues(formFieldName);
    const formArrayValues = isArray(formValues) ? formValues : [];
    if (JSON.stringify(profilesValues) !== JSON.stringify(formArrayValues)) {
      setFormValue(formFieldName, profilesValues);
    }
  }, [allProfilesValues, setFormValue, formFieldName, getFormValues]);

  const counterProfiles = selectedProfiles?.length || 0;

  const inputID = useMemo(() => idGenerator(), []);
  // this id is used for the main select all checkbox

  let group: JSX.Element[] = [];
  for (const [key, value] of Array.from(profilesGroups.entries())) {
    const labelNetwork = key.split('SocialProfile')[0];
    const label = labelNetwork.toLowerCase() === SocialNetworksEnum.twitter ? 'All X (Twitter)' : `All ${labelNetwork}`;
    const list = (
      <CheckboxList
        selectAll={true}
        selectAllLabel={label}
        key={key}
        checkboxesChildrenGroupName={`${props.formFieldName}_${key}`}
        selectAllName={`select_all_${key}_profiles`}
        selectAllParentName={props.formFieldName}
        selectAllSize={'small'}
        borderless
        wrapperClickable
        formInstance={props.useFormInstance}
        stopPropagation={true}
        label={''}>
        {value.map((profile, index) => {
          const formName = `${props.formFieldName}_${key}.${index}`;
          return (
            <SocialProfileSelectorListItem
              disabled={props.disabledProfileIds.includes(profile.id)}
              key={`${profile.id}`}
              useFormInstance={props.useFormInstance}
              useFormName={formName}
              useFormValue={profile.id}
              socialProfileComponent={
                <SocialProfile
                  socialProfile={profilesHelpers.remapSocialProfile(profile, false, true, true)}
                  size={'small'}
                  checkbox
                  useFormInstance={props.useFormInstance}
                  label
                  sublabel={false}
                  {...(profile.__typename !== 'Bitly' && (profile as SocialProfileFieldsFragment).photoUrl
                    ? { image: (profile as SocialProfileFieldsFragment).photoUrl }
                    : {})}
                  useFormName={formName}
                  {...(props.pinterestFormName
                    ? {
                        pinterestBoard: true,
                        pinterestFormName: `${props.pinterestFormName}.${index}`,
                        //we use `index` to map for main profile here. We need to make sure we compare against main profile
                        // form name values on submit, in order to properly map pn boards to selected profiles
                        pinterestRules: props.pinterestRules,
                        ...(!!pnSelectedBoard(profile)
                          ? { pinterestBoardValue: pnSelectedBoard(profile) as string }
                          : {})
                      }
                    : { pinterestBoard: false })}
                  {...(props.tikTokFormName && profile.__typename === 'TikTokSocialProfile'
                    ? {
                        tikTokSettings: true,
                        tikTokFormName: `${props.tikTokFormName}.${index}`,
                        // we use `index` to map for main profile here. We need to make sure we compare against main profile form name values on submit, in order to properly map pn boards to selected profiles
                        tikTokTargetRules: props.tikTokTargetRules,
                        tikTokActionRules: props.tikTokActionRules,
                        tikTokPromotionRules: props.tikTokPromotionRules,
                        ...(!!tiktokValues(profile) ? { ...tiktokValues(profile) } : {})
                      }
                    : { tikTokSettings: false })}
                  rules={{}}
                  value={profile.id}
                  disconnected={!profile.connected}
                  {...(isSelectedProfile(profile) ? { checked: true } : {})}
                />
              }
            />
          );
        })}
      </CheckboxList>
    );
    group = [...group, <>{list}</>];
  }

  const clearSelected = useCallback(() => {
    profileGroupsKeys.forEach(group => {
      const current = getFormValues(group);
      setFormValue(
        group,
        current.map(() => false)
      );
    });
  }, [setFormValue, getFormValues, profileGroupsKeys]);

  return props.profiles.length ? (
    <>
      <div className={'grid-x align-justify align-middle margin-bottom-small-16'}>
        <div className={classNames(classes.CounterSelector, 'cell', 'shrink')}>{counterProfiles} profiles selected</div>
        {counterProfiles ? (
          <div className={classNames('cell', 'shrink')}>
            <Button
              type={'button'}
              variant={'clear'}
              size={'small'}
              color={ButtonColorsEnum.primary}
              action={clearSelected}>
              clear
            </Button>
          </div>
        ) : null}
      </div>
      <SelectAllCheckboxes
        wrapperClickable
        wrapper={true}
        id={inputID}
        formFieldName={profileGroupsKeys}
        formFieldNamePrincipal={formFieldName}
        formInstance={useFormInstance}
        label={'Select All'}
        size={'small'}
        name={'select_all_profiles'}
        {...(props.selectAllValidation ? { rules: selectAllValidation } : {})}
      />
      <hr className={classes.Hr} />
      {group.map((item, index) => (
        <Fragment key={index}>
          {item}
          {group.length > index + 1 ? <hr className={classes.Hr} /> : null}
        </Fragment>
      ))}
    </>
  ) : (
    <>
      No Profiles connected.
      <br className={'show-for-medium'} />
      <Link to={'/settings/profiles'} target={'_blank'}>
        Connect a Profile
      </Link>{' '}
      to add Posts
    </>
  );
};
